import { Language, Subsidiary } from '@coolblue-development/constants';
import { BlocksProps } from '@coolblue-development/next-hemingway';
import { SitemapNavigation } from '@features/filter/types';
import { ReactNode } from 'react';

// SITEMAP SERVICE

export type NodeProps = {
  nodeId: string;
  parentNodeId?: string | null;
  title: string;
  presentationFormat: ArticlePresentationFormat;
  childPageLinks: ChildPageLink[];
  contentType: ArticleContentType;
  relativeUrl: string;
  isClosedCommunity: boolean;
  // Metadata
  metaDescription: string | undefined;
  hrefLangs: HrefLang[];
  canonicalUrl: string | undefined;
  structuredData: string | null;
  // SEO
  browserTitle?: string;
  contentIndexable: boolean;
  urlIndexable: boolean;
  // Article
  article: Article | undefined;
  draftNode: { article: Article | null } | undefined;
  navigation: SitemapNavigation | undefined;
};

export type SearchResultProps = {
  appliedFilters: AppliedFilter;
};

export type AppliedFilter = {
  productTypeIds: string[];
  brandIds: string[];
};

export type ChildPageLink = {
  title: string;
  relativeUrl: string;
  presentationFormat: ArticlePresentationFormat;
};

type HrefLang = {
  url: string;
  hrefLang: string;
};

export type NodeRepositoryProps = {
  nodeId: string;
  subsidiary: Subsidiary;
  language: Language;
  isInternalUser?: boolean;
  shouldGetDraft?: boolean;
  input?: HemingwayBlockDimensionsInput;
};

export type SearchResultRepositoryProps = {
  path: string;
};

export type GetNodeQueryResponseType = {
  sitemapNodeById: {
    id: string;
    parent: { id: string } | null;
    title: string;
    contentType: ArticleContentType;
    relativeUrl: string;
    isClosedCommunity: boolean;
    presentationFormat: ArticlePresentationFormat;
    childPageLinks: ChildPageLink[] | null;
    metaDescription: string | null;
    hrefLangs: HrefLang[] | null;
    structuredData: string | null;
    canonicalUrl: string | null;
    browserTitle: string | null;
    contentIndexable: boolean;
    urlIndexable: boolean;
    article: Article | null;
    draftNode: { article: Article | null } | null;
    navigation: SitemapNavigation;
  } | null;
};

export type GetSearchResultQueryResponseType = {
  searchResult: {
    appliedFilters: AppliedFilter;
  } | null;
};

// SUPERGRAPH

export type Article = {
  id: string;
  blocks: BlocksProps[];
  criticalBlocks: string[];
  lastModified: string;
};

export enum ArticlePresentationFormat {
  AdviceHub = 'AdviceHub',
  AdvicePage = 'AdvicePage',
  BrandedPage = 'BrandedPage',
  CorporatePage = 'CorporatePage',
  FilterPresentation = 'FilterPresentation',
  VisualFilterPresentation = 'VisualFilterPresentation',
  CoolbluesChoice = "Coolblue's choice",
  GenericPage = 'GenericPage',
  HomepagePresentation = 'HomepagePresentation',
  StorePage = 'StorePage',
  Top10Presentation = 'Top10Presentation',
  VisualPresentation = 'VisualPresentation'
}

export enum ArticleContentType {
  Accessory = 'Accessory page',
  Advice = 'Advice page',
  Brand = 'Brand page',
  BrandCampaign = 'Brand campaign page',
  BrandProductType = 'Brand product type page',
  BrandProductTypeCampaign = 'Brand product type campaign page',
  BrandProductTypeSegment = 'Brand product type segment page',
  BrandProductTypeSegmentCampaign = 'Brand product type segment campaign page',
  Campaign = 'Campaign page',
  Category = 'Category page',
  CategoryGroup = 'Category group page',
  Corporate = 'Corporate page',
  ProductCollection = 'Product collection page',
  ProductType = 'Product type page',
  ProductTypeCampaign = 'Product type campaign page',
  ProductTypeSegment = 'Product type segment page',
  ProductTypeSegmentCampaign = 'Product type segment campaign page',
  SelfService = 'Self service page',
  Other = 'other'
}

export type HemingwayBlockDimensionsInput = {
  customerType: CustomerType;
  deviceType: DeviceType;
};

export type VirtualAgentContent = {
  title: string;
  callToAction: string;
  explanation: string;
  avatar: ReactNode;
};

export enum CustomerType {
  CONSUMER = 'CONSUMER',
  BUSINESS_CONSUMER = 'BUSINESS_CONSUMER',
  B2B = 'B2B'
}

export type DeviceType = 'DESKTOP' | 'MOBILE' | 'TABLET';
